import { ActivatedRoute, Router } from '@angular/router';
import { AfterViewInit, Component, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { NgForm } from '@angular/forms';
import { zip } from 'rxjs';
import * as FileSaver from 'file-saver';

import { BasePage } from '../../base.page';
import {
    ClientRelation,
    ClientType,
    GetClientUseCase,
    GetContractUseCase,
    GetContractsUseCase,
    GetDocumentContentUseCase,
    GetParticipantsUseCase,
    IdInterface,
    UpdateClientUseCase,
    UpdateValidationUseCase,
    UserInterface,
    ValidationStatus,
} from '../../../../core';
import { Contract, ContractLite, Document, FullClient, Id } from '../../../domain/data';
import { ErrorDialog } from '../../../ui/dialog/error/error.dialog';
import { FullDataPage } from '../full-data.page';
import { FullDataSevenAdminPbcComponent } from '../../../ui/full-data/seven-admin-pbc/full-data-seven-admin-pbc.component';
import { MainRoutes } from '../../../main-routes.enum';
import { ParentEvents } from '../../../utils/parent-events.service';
import { ValidationReasonDialog } from '../../../ui/dialog/validation-reason/validation-reason.dialog';
import { delay } from 'rxjs/operators';

@Component({
    templateUrl: './full-data-profile.page.html',
    styleUrls: ['./full-data-profile.page.scss'],
})
export class FullDataProfilePage extends BasePage implements AfterViewInit, OnInit {
    public ClientRelation: typeof ClientRelation = ClientRelation;
    public ClientType: typeof ClientType = ClientType;
    public MainRoutes: typeof MainRoutes = MainRoutes;

    public contract: Contract;
    public participant: FullClient;
    public participants: FullClient[] = [];
    public clientId: string;
    public contracts: ContractLite[] = [];

    private stepsViewed: string[] = ['one'];
    private downloadedDocuments: string[] = [];

    public isDisabledProfessional: boolean = false;
    public isDisabledDuties: boolean;

    @ViewChildren(NgForm)
    forms: QueryList<NgForm>;

    @ViewChildren(MatExpansionPanel)
    panels: QueryList<MatExpansionPanel>;

    @ViewChild('adminPbcComponent')
    adminPbcComponent: FullDataSevenAdminPbcComponent;

    constructor(
        parentEvents: ParentEvents,
        private dialog: MatDialog,
        private fullDataPage: FullDataPage,
        private getClientUseCase: GetClientUseCase,
        private getContractUseCase: GetContractUseCase,
        private getContractsUseCase: GetContractsUseCase,
        private getDocumentContentUseCase: GetDocumentContentUseCase,
        private getParticipantsUseCase: GetParticipantsUseCase,
        private route: ActivatedRoute,
        private router: Router,
        private updateClientUseCase: UpdateClientUseCase,
        private updateValidationUseCase: UpdateValidationUseCase
    ) {
        super(parentEvents);
        fullDataPage.showBackButton();
    }

    get user(): UserInterface {
        return this.fullDataPage.user;
    }

    get canFinalizeContract(): boolean {
        return (
            this.contract &&
            this.participants &&
            this.participants.length > 0 &&
            this.contract.isComplete() &&
            !this.participants.some((participant) => !participant.isComplete(this.contract.isAdvisoryModelGDC()))
        );
    }

    get canRevise(): boolean {
        return this.participant && !this.participant.isValidated();
    }

    get canReject(): boolean {
        return this.participant && !this.participant.isValidated();
    }

    get canValidate(): boolean {
        return (
            this.participant &&
            !this.participant.isValidated() &&
            this.panels &&
            this.panels.length === this.stepsViewed.length &&
            this.participant.documents.filter((document) => !!document.id).length === this.downloadedDocuments.length
        );
    }

    get isDisabled(): boolean {
            return (
                this.user?.isAdmin ||
                this.participant?.isValidated() ||
                this.contract?.isRejected() ||
                (this.participant.type === ClientType.NATURAL_PERSON && this.contract?.isAdvisoryModelGDC())
            );
    }

    ngOnInit() {
        this.route.params.subscribe((params) => {
            if (params.id) {
                this.getClient(Id.fromString(params.id));
            }
        });

        this.getContract();
        this.getParticipants();
    }

    ngOnDestroy(): void {
        localStorage.removeItem('IsTitular');
    }

    private getClient(code: IdInterface) {
        this.getClientUseCase.bind({ code }).subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.participant = transaction.data as FullClient;
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog, {
                    data: { text: transaction.error },
                });
            }
        });
    }

    private getContract() {
        this.getContractUseCase.bind().subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.contract = transaction.data as Contract;
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog);
            }
        });
    }

    private getParticipants() {
        this.getParticipantsUseCase.bind({ isFull: true }).subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.participants = transaction.data as FullClient[];
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog);
            }
        });
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
        this.handleFormStatusChanges();
    }

    private handleFormStatusChanges(): void {
        const updatePanelNumber = (panels: QueryList<MatExpansionPanel>) => {
            panels.forEach((panel, i) => {
                document.querySelectorAll(`#${panel._headerId} .num`).forEach((item, j) => {
                    item.innerHTML = `${i + 1}.`;
                });

                document.querySelectorAll(`#${panel.id} .num`).forEach((item, j) => {
                    item.innerHTML = `${i + 1}.${j + 1}.`;
                });
            });

            setTimeout(() => {
                if (this.adminPbcComponent) {
                    this.adminPbcComponent.number = panels.length;
                }
            });
        };

        this.panels.changes.subscribe((panels: QueryList<MatExpansionPanel>) => updatePanelNumber(panels));
        updatePanelNumber(this.panels);
    }

    onAccordionChange(section: string) {
        if (this.stepsViewed.indexOf(section) === -1) {
            this.stepsViewed.push(section);
        }
        this.parentEvents.resize();
    }

    onSubmit() {
        this.updateClientUseCase.bind({ client: this.participant }).subscribe((transaction) => {
            transaction.fold(
                () => {
                    if (this.participant.isComplete(this.contract.isAdvisoryModelGDC())) {
                        this.router.navigateByUrl(MainRoutes.FULL_DATA);
                    } else {
                        this.getClient(this.participant.encryptedCode);
                        this.nextStep();
                    }
                },
                (throwable) =>
                    this.dialog.open(ErrorDialog, {
                        data: { text: throwable.message },
                    })
            );
        });
    }

    nextStep() {
        let found = false;
        this.forms.map((form, idx) => {
            if (!form.valid && !found) {
                if (this.panels.toArray()[idx]) {
                    found = true;
                    this.panels.toArray()[idx].open();
                }
            }
        });
    }

    onDocumentView(document: Document) {
        this.getDocumentContentUseCase.bind({ document }).subscribe((transaction) => {
            if (!!document.id && this.downloadedDocuments.indexOf(String(document.id)) === -1) {
                this.downloadedDocuments.push(String(document.id));
            }

            if (transaction.isSuccess) {
                FileSaver.saveAs(transaction.data, document.filename);
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog, {
                    data: { text: transaction.error },
                });
            }
        });
    }

    onRevise() {
        const dialogRef = this.dialog.open(ValidationReasonDialog, {
            data: {
                text: 'dialog.revise-client',
                validation: this.participant.validation,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.participant.validation.status = ValidationStatus.REVISE;
                this.updateValidationAndRedirect();
            }
        });
    }

    private updateValidationAndRedirect(isPBC = false) {
        const observables = [this.updateValidationUseCase.bind({ client: this.participant })];

        if (isPBC) {
            this.contract.validation.status =
                !this.participant.validation.isValid() ||
                this.participants
                    .filter((it) => !this.participant.id.equals(it.id) && it.hasPBCPending)
                    .some((it) => !it.validation.isValid())
                    ? ValidationStatus.REJECT
                    : ValidationStatus.VALID;
            observables.push(this.updateValidationUseCase.bind({ contract: this.contract }));
        }

        zip(...observables).subscribe(() => {
            this.router.navigateByUrl(MainRoutes.FULL_DATA);
        });
    }

    onReject(isPBC = false) {
        const dialogRef = this.dialog.open(ValidationReasonDialog, {
            data: {
                text: 'dialog.reject-client',
                validation: this.participant.validation,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.participant.validation.status = ValidationStatus.REJECT;
                this.updateValidationAndRedirect(isPBC);
            }
        });
    }

    onValidate(isPBC = false) {
        this.participant.validation.status = ValidationStatus.VALID;
        this.updateValidationAndRedirect(isPBC);
    }
}
